import { Injectable } from "@angular/core";
import * as signalR from "@microsoft/signalr";
import { fetchAuthSession } from "aws-amplify/auth";
import { Observable, Subject } from "rxjs";
import { environment } from "src/environments/environment";
import { SmPortalMessage } from "./secure-message.model";

/** SignalR SecureMessageAdded payload (host-relative is_mine on message). */
export interface SmRealtimeEvent {
  thread_key: number;
  public_code: string;
  state_cd: string;
  is_host: boolean;
  message: SmPortalMessage;
}

type JoinMode =
  | { kind: "thread"; threadKey: number }
  | { kind: "session"; sessionToken: string }
  | null;

/**
 * Dedicated hub for secure messaging (not ChatHub).
 * FRM: Cognito access_token. Practice: anonymous + JoinBySession.
 */
@Injectable({ providedIn: "root" })
export class SecureMessageRealtimeService {
  private hub: signalR.HubConnection | null = null;
  private started = false;
  private joinMode: JoinMode = null;
  private readonly events$ = new Subject<SmRealtimeEvent>();
  private authMode: "cognito" | "anon" = "cognito";

  onMessage(): Observable<SmRealtimeEvent> {
    return this.events$.asObservable();
  }

  /** FRM Messages — Cognito JWT. */
  async connectAuthenticated(): Promise<void> {
    this.authMode = "cognito";
    await this.ensureConnection();
  }

  /** Practice portal — no JWT. */
  async connectAnonymous(): Promise<void> {
    this.authMode = "anon";
    await this.ensureConnection();
  }

  async joinThread(threadKey: number): Promise<void> {
    if (!threadKey) return;
    this.joinMode = { kind: "thread", threadKey };
    await this.ensureConnection();
    await this.hub!.invoke("JoinThread", threadKey);
  }

  async joinBySession(sessionToken: string): Promise<void> {
    if (!sessionToken?.trim()) return;
    this.joinMode = { kind: "session", sessionToken: sessionToken.trim() };
    await this.ensureConnection();
    await this.hub!.invoke("JoinBySession", sessionToken.trim());
  }

  async leaveThread(threadKey: number): Promise<void> {
    if (!this.hub || !threadKey) return;
    try {
      await this.hub.invoke("LeaveThread", threadKey);
    } catch {
      /* ignore */
    }
    if (this.joinMode?.kind === "thread" && this.joinMode.threadKey === threadKey) {
      this.joinMode = null;
    }
  }

  async stop(): Promise<void> {
    this.joinMode = null;
    if (!this.hub) return;
    try {
      await this.hub.stop();
    } catch {
      /* ignore */
    }
    this.hub = null;
    this.started = false;
  }

  private async ensureConnection(): Promise<void> {
    if (this.hub && this.started && this.hub.state === signalR.HubConnectionState.Connected) {
      return;
    }
    if (this.hub && this.hub.state === signalR.HubConnectionState.Connecting) {
      await this.waitUntilConnected();
      return;
    }

    if (this.hub) {
      try {
        await this.hub.stop();
      } catch {
        /* ignore */
      }
      this.hub = null;
      this.started = false;
    }

    const builder = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.apiBaseUrl}/secureMessageHub`, this.hubOptions())
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning);

    this.hub = builder.build();
    this.hub.on("SecureMessageAdded", (payload: SmRealtimeEvent) => {
      if (payload?.message?.message_key != null) this.events$.next(payload);
    });
    this.hub.onreconnected(async () => {
      this.started = true;
      await this.rejoin();
    });
    this.hub.onclose(() => {
      this.started = false;
    });

    await this.hub.start();
    this.started = true;
    await this.rejoin();
  }

  private hubOptions(): signalR.IHttpConnectionOptions {
    if (this.authMode === "anon") {
      return {};
    }
    return {
      accessTokenFactory: async () => {
        try {
          const session: any = await fetchAuthSession();
          return session?.tokens?.idToken?.toString?.() ?? session?.tokens?.idToken ?? "";
        } catch {
          return "";
        }
      },
    };
  }

  private async rejoin(): Promise<void> {
    if (!this.hub || !this.joinMode) return;
    try {
      if (this.joinMode.kind === "thread") {
        await this.hub.invoke("JoinThread", this.joinMode.threadKey);
      } else {
        await this.hub.invoke("JoinBySession", this.joinMode.sessionToken);
      }
    } catch (err) {
      console.warn("SecureMessageHub rejoin failed", err);
    }
  }

  private waitUntilConnected(): Promise<void> {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const tick = () => {
        if (this.hub?.state === signalR.HubConnectionState.Connected) {
          resolve();
          return;
        }
        if (Date.now() - start > 15000) {
          reject(new Error("SignalR connect timeout"));
          return;
        }
        setTimeout(tick, 100);
      };
      tick();
    });
  }
}
